import React, { useState, useEffect } from 'react';
import './MultiOriginSettings.css';


// Direct API calls without using api service
// IMPORTANT: Use the correct backend URL from environment variable
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY || 'HX4rA9!dj7%pQa12zLbT3V';

if (!API_BASE_URL) {
  console.error('[MultiOrigin] REACT_APP_BACKEND_URL is not set! Multi-origin settings will not work.');
}

// ─── Destination options shown in the per-vendor dropdown ───────────────────
const DESTINATION_OPTIONS = [
  { value: 'ALL',  label: 'All Countries' },
  { value: 'EU',   label: 'European Union (EU27)' },
  { value: 'GB',   label: 'United Kingdom' },
  { value: 'US',   label: 'United States' },
  { value: 'CA',   label: 'Canada' },
  { value: 'AU',   label: 'Australia' },
  { value: 'NZ',   label: 'New Zealand' },
  { value: 'JP',   label: 'Japan' },
  { value: 'SG',   label: 'Singapore' },
  { value: 'NO',   label: 'Norway' },
  { value: 'CH',   label: 'Switzerland' },
  // Individual EU destinations are essential for carriers such as Royal Mail,
  // where DDP availability can differ between EU member states.
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'HR', label: 'Croatia' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'DK', label: 'Denmark' },
  { value: 'EE', label: 'Estonia' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'GR', label: 'Greece' },
  { value: 'HU', label: 'Hungary' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IT', label: 'Italy' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'MT', label: 'Malta' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Romania' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'ES', label: 'Spain' },
  { value: 'SE', label: 'Sweden' },
];
const EU_DDP_COUNTRY_CODES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'
]);

// Human-readable label for a destinations list
const destinationsLabel = (dests) => {
  if (!dests || dests.length === 0) return '—';
  if (dests.includes('ALL')) return 'All Countries';
  return dests.map(d => {
    const opt = DESTINATION_OPTIONS.find(o => o.value === d);
    return opt ? opt.label : d;
  }).join(', ');
};

/**
 * Multi-Origin Settings Component
 * Allows Growth+ merchants to configure vendor-based and metafield-based origin mapping
 */
const MultiOriginSettings = ({ shop, planName, currentOrigin, countryOptions, onUpgrade, getSessionToken, onOriginModeSaved }) => {
  // Origin countries are independent of customer delivery destinations.
  const originCountryOptions = countryOptions.some(country => country.code === 'VN')
    ? countryOptions
    : [...countryOptions, { code: 'VN', name: '🇻🇳 Vietnam' }];
  // State
  const [config, setConfig] = useState(null);
  const [allVendorsFromAPI, setAllVendorsFromAPI] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pagination state (client-side)
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorLimit] = useState(12);

  // Search/Filter state
  const [vendorSearch, setVendorSearch] = useState('');

  // Form state
  const [originMode, setOriginMode] = useState('single');
  const [defaultOrigin, setDefaultOrigin] = useState(currentOrigin || 'US');
  const [vendorMap, setVendorMap] = useState({});

  // v2 TCA preferences: { "VendorName": ["EU", "GB"] }
  // An empty array [] means zero-rate is OFF for that vendor.
  // tcaEnabled state tracks which vendors have the checkbox ticked
  // (needed because destinations can be empty while checkbox is still ON)
  const [tcaPreferences, setTcaPreferences] = useState({});
  const [vendorDdpCountries, setVendorDdpCountries] = useState({});
  const [vendorFeeCollectionEnabled, setVendorFeeCollectionEnabled] = useState(false);
  const [vendorFeeCollectionBusy, setVendorFeeCollectionBusy] = useState(false);
  const [vendorFeeCollectionStatusLoaded, setVendorFeeCollectionStatusLoaded] = useState(false);
  const [tcaEnabled, setTcaEnabled] = useState({}); // { vendor: bool }

  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [metafieldConfig, setMetafieldConfig] = useState({
    enabled: false,
    scope: 'product',
    namespace: 'custom',
    key: 'origin_country'
  });
  const [visibilityRules, setVisibilityRules] = useState({
    hide_for_vendors: []
  });
  const [showTcaDisclaimer, setShowTcaDisclaimer] = useState(false);
  // New/unconfigured vendors: vendors in the store but not yet assigned an origin
  const [newVendors, setNewVendors] = useState([]);
  const [dismissedNewVendors, setDismissedNewVendors] = useState(false);

  // Plan access check
  const hasMultiOriginAccess = () => {
    const allowedPlans = ['growth', 'enterprise', 'trial', 'basic'];
    const normalizedPlan = (planName || '').toLowerCase().trim();
    return allowedPlans.includes(normalizedPlan);
  };

  const hasEnterpriseDdpAccess = ['enterprise', 'plus'].includes((planName || '').toLowerCase().trim());

  // Load configuration
  useEffect(() => {
    if (shop) {
      fetchConfig();
    } else {
      console.warn('🔧 MultiOriginSettings: No shop parameter provided');
      setLoading(false);
      setError('Shop parameter is missing. Please ensure the app is loaded within Shopify Admin.');
    }
  }, [shop]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      setVendorFeeCollectionStatusLoaded(false);

      const url = `${API_BASE_URL}/api/multi-origin/config?shop=${encodeURIComponent(shop)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();

      setConfig(data.multi_origin_config);
      setOriginMode(data.multi_origin_config.enabled ? 'multi' : 'single');
      setDefaultOrigin(data.multi_origin_config.default_origin || currentOrigin || 'US');
      setVendorMap(data.multi_origin_config.vendor_origin_map || {});

      // Backend normalises legacy booleans → list format before sending
      // So we always receive { "VendorName": ["EU", "GB"] } or { "VendorName": [] }
      const tcaPrefs = data.multi_origin_config.vendor_tca_preferences || {};
      setTcaPreferences(tcaPrefs);
      setVendorDdpCountries(data.multi_origin_config.vendor_ddp_countries || {});
      // Reuse the same DDP service-product setting as Single-Origin, but
      // display it alongside the vendor-specific destination controls.
      fetch(`${API_BASE_URL}/api/ddp-settings/${encodeURIComponent(shop)}`, {
        headers: {'x-api-key': API_KEY}
      }).then(response => response.ok ? response.json() : null)
        .then(settings => {
          if (typeof settings?.multi_origin_ddp_fee_collection_enabled === 'boolean') {
            // Destination selections express eligibility only. The backend's
            // confirmed flag is the sole source of the collection state.
            setVendorFeeCollectionEnabled(settings.multi_origin_ddp_fee_collection_enabled);
            setVendorFeeCollectionStatusLoaded(true);
          } else {
            setError('Unable to read the current Multi-Origin DDP collection setting.');
          }
        }).catch(() => {
          setError('Unable to read the current Multi-Origin DDP collection setting.');
        });

      // Derive enabled state: a vendor is "on" if its list is non-empty
      const enabledMap = {};
      Object.entries(tcaPrefs).forEach(([v, dests]) => {
        enabledMap[v] = Array.isArray(dests) && dests.length > 0;
      });
      setTcaEnabled(enabledMap);

      setMetafieldConfig(data.multi_origin_config.origin_metafield || {
        enabled: false,
        scope: 'product',
        namespace: 'custom',
        key: 'origin_country'
      });
      setVisibilityRules(data.multi_origin_config.calculator_visibility_rules || {
        hide_for_vendors: []
      });

      if (hasMultiOriginAccess()) {
        fetchVendors();
      }

    } catch (err) {
      console.error('❌ Failed to fetch multi-origin config:', err);
      const errorMsg = err.data?.detail || err.message || 'Failed to load multi-origin settings';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    setVendorsLoading(true);
    try {
      const url = `${API_BASE_URL}/api/multi-origin/vendors?shop=${encodeURIComponent(shop)}&page=1&limit=1000`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const vendors = data.vendors || [];
      setAllVendorsFromAPI(vendors);
      // Detect vendors that exist in the store but have no origin assigned yet
      // We compare against the current vendorMap (set by fetchConfig)
      setVendorMap(prev => {
        const unconfigured = vendors.filter(v => !(v in prev) || !prev[v]);
        setNewVendors(unconfigured);
        setDismissedNewVendors(false); // Reset dismiss on each refresh
        return prev;
      });
    } catch (err) {
      console.error('❌ Failed to fetch vendors:', err);
      setAllVendorsFromAPI([]);
    } finally {
      setVendorsLoading(false);
    }
  };

  // Auto-save with debounce (500ms)
  const triggerAutoSave = (configToSave, autoEnableCollection = false) => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    const timer = setTimeout(() => {
      handleSave(configToSave, autoEnableCollection);
    }, 500);
    setAutoSaveTimer(timer);
  };

  const handleSave = async (configOverride = null, autoEnableCollection = false) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = configOverride || {
        enabled: originMode === 'multi' && hasMultiOriginAccess(),
        mode: originMode === 'multi' ? 'vendor' : 'single',
        default_origin: defaultOrigin,
        vendor_origin_map: originMode === 'multi' ? vendorMap : {},
        vendor_tca_preferences: tcaPreferences,
        vendor_ddp_countries: vendorDdpCountries,
        origin_metafield: originMode === 'multi' && showAdvanced ? metafieldConfig : { enabled: false },
        calculator_visibility_rules: originMode === 'multi' ? visibilityRules : { hide_for_vendors: [] }
      };

      const url = `${API_BASE_URL}/api/multi-origin/config?shop=${encodeURIComponent(shop)}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const failure = await response.json().catch(() => null);
        const detail = failure?.detail;
        throw new Error(typeof detail === 'string' ? detail : `Unable to save settings (HTTP ${response.status}).`);
      }

      setSuccess('✅ Multi-origin settings saved successfully!');
      setTimeout(() => setSuccess(null), 5000);

      // Do not reload the entire panel after autosave. fetchConfig() changes
      // loading state, unmounts the vendor table and jumps the viewport.
      // Local controlled state already holds the saved settings.
      setConfig(payload);
      // Keep the parent DDP panel in sync without requiring a page refresh.
      // Only notify after the backend has accepted the mode change.
      onOriginModeSaved?.(Boolean(payload.enabled));
      const hasVendorDdp = Boolean(
        payload.enabled && Object.values(payload.vendor_ddp_countries || {})
          .some(countries => Array.isArray(countries) && countries.length > 0)
      );
      // Never activate collection as a side effect of saving configuration or
      // choosing a destination. If an active collection loses its final
      // eligible destination, turn it off as a safety measure only.
      if (!hasVendorDdp && vendorFeeCollectionEnabled) {
        await syncVendorFeeCollection(false);
      } else if (hasVendorDdp && autoEnableCollection && hasEnterpriseDdpAccess &&
                 vendorFeeCollectionStatusLoaded && !vendorFeeCollectionEnabled) {
        // Only auto-activate after the destination configuration is saved.
        await syncVendorFeeCollection(true);
      }

    } catch (err) {
      console.error('Failed to save multi-origin config:', err);
      setError(err.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleVendorOriginChange = (vendor, origin) => {
    const newVendorMap = { ...vendorMap };
    if (origin === '' || origin === 'default') {
      delete newVendorMap[vendor];
    } else {
      newVendorMap[vendor] = origin;
    }
    setVendorMap(newVendorMap);

    triggerAutoSave({
      enabled: originMode === 'multi' && hasMultiOriginAccess(),
      mode: 'vendor',
      default_origin: defaultOrigin,
      vendor_origin_map: newVendorMap,
      vendor_tca_preferences: tcaPreferences,
      vendor_ddp_countries: vendorDdpCountries,
      origin_metafield: metafieldConfig,
      calculator_visibility_rules: visibilityRules
    });
  };

  // ─── Zero-rate duty handlers (v2 destination-list schema) ────────────────

  /**
   * Toggle zero-rate duty on/off for a vendor.
   * When turning ON: start with an empty destination list — merchant picks their own.
   * When turning OFF: clear the list.
   */
  const handleTcaToggle = (vendor) => {
    const wasEnabled = tcaEnabled[vendor] || false;
    const nowEnabled = !wasEnabled;
    setTcaEnabled(prev => ({ ...prev, [vendor]: nowEnabled }));

    if (!nowEnabled) {
      // Turning OFF: clear destinations and save immediately
      const newTcaPreferences = { ...tcaPreferences, [vendor]: [] };
      setTcaPreferences(newTcaPreferences);
      triggerAutoSave({
        enabled: originMode === 'multi' && hasMultiOriginAccess(),
        mode: 'vendor',
        default_origin: defaultOrigin,
        vendor_origin_map: vendorMap,
        vendor_tca_preferences: newTcaPreferences,
      vendor_ddp_countries: vendorDdpCountries,
        origin_metafield: metafieldConfig,
        calculator_visibility_rules: visibilityRules
      });
    }
    // Turning ON: do NOT autosave yet — wait for merchant to add a destination
  };

  /**
   * Add a destination specifier to a vendor's zero-rate list.
   * If "ALL" is added, clear everything else and just store ["ALL"].
   */
  const handleAddDestination = (vendor, dest) => {
    if (!dest) return;
    const current = tcaPreferences[vendor] || [];
    let updated;
    if (dest === 'ALL') {
      updated = ['ALL'];
    } else if (current.includes('ALL')) {
      // Already all countries — no-op
      return;
    } else if (current.includes(dest)) {
      // Already present — no-op
      return;
    } else {
      updated = [...current, dest];
    }
    const newTcaPreferences = { ...tcaPreferences, [vendor]: updated };
    setTcaPreferences(newTcaPreferences);

    triggerAutoSave({
      enabled: originMode === 'multi' && hasMultiOriginAccess(),
      mode: 'vendor',
      default_origin: defaultOrigin,
      vendor_origin_map: vendorMap,
      vendor_tca_preferences: newTcaPreferences,
      vendor_ddp_countries: vendorDdpCountries,
      origin_metafield: metafieldConfig,
      calculator_visibility_rules: visibilityRules
    });
  };

  /**
   * Remove a destination specifier from a vendor's zero-rate list.
   */
  const handleRemoveDestination = (vendor, dest) => {
    const current = tcaPreferences[vendor] || [];
    const updated = current.filter(d => d !== dest);
    const newTcaPreferences = { ...tcaPreferences, [vendor]: updated };
    setTcaPreferences(newTcaPreferences);

    triggerAutoSave({
      enabled: originMode === 'multi' && hasMultiOriginAccess(),
      mode: 'vendor',
      default_origin: defaultOrigin,
      vendor_origin_map: vendorMap,
      vendor_tca_preferences: newTcaPreferences,
      vendor_ddp_countries: vendorDdpCountries,
      origin_metafield: metafieldConfig,
      calculator_visibility_rules: visibilityRules
    });
  };

  const changeVendorDdp = (vendor, destination, add) => {
    if (!destination || !hasEnterpriseDdpAccess) return;
    const previous = vendorDdpCountries[vendor] || [];
    const next = add
      ? (destination === 'ALL'
          ? ['ALL']
          : destination === 'EU'
            ? [...previous.filter(x => !EU_DDP_COUNTRY_CODES.has(x) && x !== 'ALL'), 'EU']
            : [...previous.filter(x => x !== 'ALL' && x !== destination &&
                !(EU_DDP_COUNTRY_CODES.has(destination) && x === 'EU')), destination])
      : previous.filter(x => x !== destination);
    const updated = { ...vendorDdpCountries, [vendor]: next };
    setVendorDdpCountries(updated);
    triggerAutoSave({
      enabled: originMode === 'multi' && hasMultiOriginAccess(),
      mode: 'vendor',
      default_origin: defaultOrigin,
      vendor_origin_map: vendorMap,
      vendor_tca_preferences: tcaPreferences,
      vendor_ddp_countries: updated,
      origin_metafield: metafieldConfig,
      calculator_visibility_rules: visibilityRules
    }, add && next.length > 0);
  };

  // Independent Multi-Origin collection is a deliberate merchant action.
  // Do not optimistically flip local state: Shopify/backend confirmation is
  // required before the checkbox reflects a changed collection setting.
  const syncVendorFeeCollection = async (enabled) => {
    setVendorFeeCollectionBusy(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ddp-settings/${encodeURIComponent(shop)}`, {
        method: 'PATCH',
        headers: {'Content-Type': 'application/json', 'x-api-key': API_KEY},
        body: JSON.stringify({multi_origin_ddp_fee_collection_enabled: enabled})
      });
      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        // Use the HTTP status below when a proxy or Shopify returns no JSON.
      }
      if (!response.ok) {
        throw new Error(data.detail || `Unable to configure Multi-Origin DDP collection (HTTP ${response.status})`);
      }
      if (typeof data.multi_origin_ddp_fee_collection_enabled !== 'boolean') {
        throw new Error('Multi-Origin DDP collection change was not confirmed by the server.');
      }
      setVendorFeeCollectionEnabled(data.multi_origin_ddp_fee_collection_enabled);
      if (data.multi_origin_ddp_fee_collection_enabled !== enabled) {
        throw new Error('Multi-Origin DDP collection change was not confirmed by the server.');
      }
      return true;
    } catch (error) {
      setError(error.message || 'Unable to configure Multi-Origin DDP collection.');
      return false;
    } finally {
      setVendorFeeCollectionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-section multi-origin-section">
        <div className="loading-spinner">Loading multi-origin settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-section multi-origin-section">
      <h3>🌐 Multi-Origin & Zero-Rate Duty Settings</h3>
      <p className="section-description" style={{color: '#666', marginBottom: '20px', lineHeight: '1.5'}}>
        Configure vendor origins worldwide. Use the <strong>Zero-Rate Duty</strong> column to apply 0% customs duty for any vendor with a qualifying trade agreement (TCA, CPTPP, free-trade zone, or other), and choose which destination countries or groups the zero rate applies to. You confirm eligibility.
      </p>

      {/* Plan Gate Notice for Starter */}
      {!hasMultiOriginAccess() && (
        <div className="alert alert-info">
          <strong>📦 Multi-Origin & Zero-Rate Duty Available on Growth Plan</strong>
          <p>
            Upgrade to Growth or Enterprise to enable multi-origin support and preferential duty rates,
            allowing you to map different vendors to different origin countries for accurate duty calculations.
          </p>
          <button
            className="btn btn-primary btn-sm"
            style={{marginTop: '0.5rem'}}
            onClick={() => onUpgrade && onUpgrade(shop)}
          >
            Upgrade to Growth →
          </button>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Origin Mode Selector */}
      <div className="form-group">
        <label>Origin Mode</label>
        <div className="radio-group">
          <label className={`radio-option ${!hasMultiOriginAccess() ? 'disabled' : ''}`}>
            <input
              type="radio"
              name="originMode"
              value="single"
              checked={originMode === 'single'}
              onChange={(e) => {
                const newMode = e.target.value;
                setOriginMode(newMode);
                triggerAutoSave({
                  enabled: false,
                  mode: 'single',
                  default_origin: defaultOrigin,
                  vendor_origin_map: vendorMap,
                  vendor_tca_preferences: tcaPreferences,
        vendor_ddp_countries: vendorDdpCountries,
                  origin_metafield: metafieldConfig,
                  calculator_visibility_rules: visibilityRules
                });
              }}
            />
            <span>Single Origin (Simple)</span>
            <small>Use one global shipping origin for all products</small>
          </label>

          <label className={`radio-option ${!hasMultiOriginAccess() ? 'disabled' : ''}`}>
            <input
              type="radio"
              name="originMode"
              value="multi"
              checked={originMode === 'multi'}
              onChange={(e) => {
                const newMode = e.target.value;
                setOriginMode(newMode);
                triggerAutoSave({
                  enabled: hasMultiOriginAccess(),
                  mode: 'vendor',
                  default_origin: defaultOrigin,
                  vendor_origin_map: vendorMap,
                  vendor_tca_preferences: tcaPreferences,
        vendor_ddp_countries: vendorDdpCountries,
                  origin_metafield: metafieldConfig,
                  calculator_visibility_rules: visibilityRules
                });
              }}
              disabled={!hasMultiOriginAccess()}
            />
            <span>Multi-Origin (Advanced) {!hasMultiOriginAccess() && '🔒'}</span>
            <small>Map vendors to different origin countries</small>
          </label>
        </div>
      </div>

      {/* Default Origin Selector */}
      <div className="form-group">
        <label>
          {originMode === 'multi' ? 'Default Origin (Fallback)' : 'Shipping Origin Country'}
          {saving && <span style={{marginLeft: '10px', color: '#666', fontSize: '12px'}}>💾 Saving...</span>}
          {success && !saving && <span style={{marginLeft: '10px', color: '#4caf50', fontSize: '12px'}}>✅ Saved</span>}
        </label>
        <select
          value={defaultOrigin}
          onChange={(e) => {
            const newOrigin = e.target.value;
            setDefaultOrigin(newOrigin);
            triggerAutoSave({
              enabled: originMode === 'multi' && hasMultiOriginAccess(),
              mode: originMode === 'multi' ? 'vendor' : 'single',
              default_origin: newOrigin,
              vendor_origin_map: vendorMap,
              vendor_tca_preferences: tcaPreferences,
        vendor_ddp_countries: vendorDdpCountries,
              origin_metafield: metafieldConfig,
              calculator_visibility_rules: visibilityRules
            });
          }}
          className="form-control"
        >
          <option value="">Select origin country...</option>
          {originCountryOptions.map(country => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        {originMode === 'multi' && (
          <small className="form-text">
            Used when vendor mapping doesn't match.
          </small>
        )}
      </div>

      {/* Vendor Mapping Table (Multi-Origin Only) */}
      {originMode === 'multi' && hasMultiOriginAccess() && (
        <div className="vendor-mapping-section">
          <h4>📦 Vendor → Origin Mapping &amp; Zero-Rate Duty</h4>
          <p className="help-text" style={{marginBottom: '12px'}}>
            Map each vendor to the country of origin used for customs calculations. Set zero-rate duty destinations per vendor only when the goods qualify for preferential treatment. Products from vendors not listed here use the default origin.
          </p>
          <div role="note" style={{
            marginBottom: '18px', padding: '12px 14px', border: '1px solid #bfdbfe',
            borderLeft: '4px solid #2563eb', borderRadius: '7px', background: '#eff6ff',
            color: '#1e3a5f', fontSize: '13px', lineHeight: 1.55
          }}>
            <strong>Manufacturing origin is not the same as dispatch country.</strong>
            <div style={{marginTop: '5px'}}>
              Use the product's actual customs origin for vendor mapping, not simply the warehouse or country it ships from.
              For example, goods manufactured in China or Vietnam but dispatched from Norway do not automatically become Norwegian-origin goods.
              Confirm the applicable rules of origin, HS code and shipping route before enabling 0% duty or DDP collection.
              The vendor-origin selector does not separately configure the dispatch country.
            </div>
          </div>

          {/* TCA Information & Disclaimer - Collapsible */}
          <div style={{
            marginBottom: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            overflow: 'hidden',
            backgroundColor: '#fafafa'
          }}>
            {/* Header - Always Visible */}
            <div
              onClick={() => setShowTcaDisclaimer(!showTcaDisclaimer)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                borderBottom: showTcaDisclaimer ? '1px solid #e0e0e0' : 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eeeeee'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span style={{fontSize: '16px'}}>📜</span>
                <strong style={{fontSize: '14px', color: '#333'}}>Zero-Rate Duty — Information &amp; Legal Disclaimer</strong>
              </div>
              <span style={{
                fontSize: '18px',
                color: '#666',
                transform: showTcaDisclaimer ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                display: 'inline-block'
              }}>
                ▼
              </span>
            </div>

            {/* Collapsible Content */}
            {showTcaDisclaimer && (
              <div style={{padding: '16px', animation: 'slideDown 0.3s ease-out'}}>
                <div style={{marginBottom: '16px'}}>
                  <h5 style={{margin: '0 0 8px 0', fontSize: '14px', color: '#1976d2'}}>
                    What is Zero-Rate Duty?
                  </h5>
                  <p style={{margin: '0 0 8px 0', fontSize: '13px', lineHeight: '1.6', color: '#555'}}>
                    Enabling zero-rate duty for a vendor tells TDK to display <strong>0% customs duty</strong> for that vendor's products when shipping to the selected destinations. VAT still applies. Use this when your products qualify under a trade agreement such as:
                  </p>
                  <ul style={{margin: '0 0 8px 0', fontSize: '13px', lineHeight: '1.8', color: '#555', paddingLeft: '20px'}}>
                    <li><strong>TCA</strong> — UK↔EU Trade and Cooperation Agreement</li>
                    <li><strong>CPTPP</strong> — Comprehensive and Progressive Agreement for Trans-Pacific Partnership</li>
                    <li><strong>AUSFTA / UKFTA</strong> — Bilateral free trade agreements</li>
                    <li>Any other qualifying preferential tariff arrangement</li>
                  </ul>
                  <p style={{margin: '0 0 8px 0', fontSize: '13px', lineHeight: '1.6', color: '#555'}}>
                    <strong>How to use:</strong> Find the vendor in the table below, check <em>Enable 0% duty</em>, then use the destination selector to choose which countries or groups (e.g. EU27, UK) the zero rate applies to. You can add multiple destinations per vendor.
                  </p>
                  <p style={{margin: '0', fontSize: '13px', lineHeight: '1.6', color: '#555'}}>
                    <strong>Destination groups:</strong> <em>European Union (EU27)</em> covers all 27 EU member states in one tick. <em>All Countries</em> applies the zero rate globally for that vendor.
                  </p>
                </div>

                {/* Legal Disclaimer */}
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  borderLeft: '4px solid #ff9800',
                  borderRadius: '4px'
                }}>
                  <h5 style={{margin: '0 0 8px 0', fontSize: '14px', color: '#e65100', display: 'flex', alignItems: 'center', gap: '6px'}}>
                    <span>⚠️</span> Legal Disclaimer — Merchant Responsibility
                  </h5>
                  <p style={{margin: '0', fontSize: '12px', lineHeight: '1.6', color: '#555'}}>
                    By enabling zero-rate duty, you acknowledge and confirm that your products meet all eligibility requirements of the applicable trade agreement or preferential tariff arrangement, including <strong>rules of origin</strong> and product standards. You are solely responsible for ensuring compliance. Products that do not qualify may be subject to customs penalties, duty charges, and border delays. This calculator provides estimates only and does not constitute customs or legal advice. Tax &amp; Duty King is not liable for customs disputes, penalties, or charges resulting from incorrect zero-rate declarations.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── New / Unconfigured Vendors Alert ─────────────────────────────── */}
          {!vendorsLoading && !dismissedNewVendors && newVendors.length > 0 && (
            <div style={{
              padding: '14px 16px',
              backgroundColor: '#fff8e1',
              border: '1px solid #ffb300',
              borderLeft: '4px solid #ffb300',
              borderRadius: '6px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <span style={{fontSize: '20px', lineHeight: '1'}}>⚠️</span>
              <div style={{flex: 1}}>
                <strong style={{color: '#e65100', fontSize: '14px'}}>
                  {newVendors.length} new vendor{newVendors.length > 1 ? 's' : ''} detected — origin not configured
                </strong>
                <p style={{margin: '4px 0 8px', fontSize: '13px', color: '#555', lineHeight: '1.5'}}>
                  The following vendor{newVendors.length > 1 ? 's have' : ' has'} been added to your store but {newVendors.length > 1 ? 'have' : 'has'} no origin country set.
                  Without an origin, duties and tariffs will use your store default. Please assign an origin below.
                </p>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px'}}>
                  {newVendors.map(v => (
                    <span key={v} style={{
                      display: 'inline-block', padding: '2px 10px',
                      backgroundColor: '#fff3e0', border: '1px solid #ffb300',
                      borderRadius: '12px', fontSize: '12px', fontWeight: '600', color: '#e65100'
                    }}>{v}</span>
                  ))}
                </div>
                <div style={{display: 'flex', gap: '8px'}}>
                  <button
                    onClick={() => {
                      setVendorSearch('');
                      setVendorPage(1);
                      // Scroll to vendor table
                      document.querySelector('.vendor-table-wrapper')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    style={{
                      padding: '5px 14px', fontSize: '12px', fontWeight: '600',
                      backgroundColor: '#ffb300', color: '#fff', border: 'none',
                      borderRadius: '4px', cursor: 'pointer'
                    }}
                  >
                    Configure now
                  </button>
                  <button
                    onClick={() => setDismissedNewVendors(true)}
                    style={{
                      padding: '5px 14px', fontSize: '12px',
                      backgroundColor: 'transparent', color: '#888',
                      border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer'
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ─────────────────────────────────────────────────────────────────── */}

          {/* Vendor Search */}
          <div className="vendor-search-container" style={{marginBottom: '1rem'}}>
            <div style={{position: 'relative', maxWidth: '400px'}}>
              <input
                type="text"
                className="form-control"
                placeholder="🔍 Search vendors..."
                value={vendorSearch}
                onChange={(e) => {
                  setVendorSearch(e.target.value);
                  setVendorPage(1);
                }}
                style={{paddingRight: vendorSearch ? '2.5rem' : '0.75rem'}}
                disabled={vendorsLoading}
              />
              {vendorSearch && !vendorsLoading && (
                <button
                  onClick={() => { setVendorSearch(''); setVendorPage(1); }}
                  style={{
                    position: 'absolute', right: '8px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', color: '#999',
                    fontSize: '1.2rem', padding: '0 4px'
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Loading Spinner for Vendors */}
          {vendorsLoading && (
            <div className="vendor-loading" style={{
              padding: '3rem', textAlign: 'center', background: '#f8f9fa',
              border: '1px solid #e0e0e0', borderRadius: '4px', marginBottom: '1rem'
            }}>
              <div className="spinner" style={{
                border: '3px solid #f3f3f3', borderTop: '3px solid #007acc',
                borderRadius: '50%', width: '40px', height: '40px',
                animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
              }}></div>
              <p style={{color: '#666', margin: 0}}>Loading vendors... This may take up to 40 seconds.</p>
              <small style={{color: '#999', display: 'block', marginTop: '0.5rem'}}>
                Fetching all vendors from your catalog...
              </small>
            </div>
          )}

          {!vendorsLoading && (() => {
            const configuredVendors = Object.keys(vendorMap || {});
            const allUniqueVendors = [...new Set([...allVendorsFromAPI, ...configuredVendors])].sort();

            let filteredVendors = allUniqueVendors;
            if (vendorSearch.trim()) {
              const searchLower = vendorSearch.toLowerCase().trim();
              filteredVendors = allUniqueVendors.filter(v => v.toLowerCase().includes(searchLower));
            }

            const totalFiltered = filteredVendors.length;
            const totalPages = Math.ceil(totalFiltered / vendorLimit);
            const startIndex = (vendorPage - 1) * vendorLimit;
            const endIndex = startIndex + vendorLimit;

            const displayVendors = vendorSearch.trim()
              ? filteredVendors
              : filteredVendors.slice(startIndex, endIndex);

            const configuredNotOnPage = vendorSearch.trim()
              ? []
              : configuredVendors.filter(v => !displayVendors.includes(v) && allVendorsFromAPI.includes(v));

            if (filteredVendors.length === 0 && vendorSearch.trim()) {
              return (
                <div className="alert alert-info">
                  <p>No vendors found matching "{vendorSearch}". Try a different search term.</p>
                </div>
              );
            }

            if (allUniqueVendors.length === 0) {
              return (
                <div className="alert alert-info">
                  <p>No vendors found. Products will be fetched once you have products in your store.</p>
                </div>
              );
            }

            return (
              <>
                {/* Search results info */}
                {vendorSearch.trim() && (
                  <div className="alert alert-info" style={{marginBottom: '0.5rem', padding: '0.5rem 0.75rem'}}>
                    <small>
                      🔍 Found {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''} matching "{vendorSearch}"
                    </small>
                  </div>
                )}

                {!vendorSearch.trim() && configuredNotOnPage.length > 0 && (
                  <div className="alert alert-info" style={{marginBottom: '0.5rem', padding: '0.5rem 0.75rem'}}>
                    <small>
                      ℹ️ Showing {configuredNotOnPage.length} configured vendor{configuredNotOnPage.length > 1 ? 's' : ''} from other pages
                    </small>
                  </div>
                )}

                <div className="vendor-table-wrapper">
                  <table className="vendor-table">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Origin Country</th>
                        <th style={{minWidth: '220px'}}>
                          Zero-Rate Duty
                          <br/>
                          <small style={{fontWeight: 'normal', color: '#666'}}>Destination(s) — e.g. EU27, UK, All</small>
                        </th>
                        <th style={{minWidth: "230px"}}>DDP destination countries</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayVendors.map(vendor => {
                        const vendorDests = tcaPreferences[vendor] || [];
                        const isUnconfigured = !vendorMap[vendor]; // No origin assigned

                        return (
                          <tr key={vendor} style={isUnconfigured ? {backgroundColor: '#fffde7'} : {}}>
                            {/* Vendor name */}
                            <td className="vendor-name">
                              {vendor}
                              {isUnconfigured && (
                                <span style={{
                                  marginLeft: '6px', fontSize: '10px', fontWeight: '700',
                                  backgroundColor: '#ffb300', color: '#fff',
                                  padding: '1px 6px', borderRadius: '8px',
                                  verticalAlign: 'middle', letterSpacing: '0.3px'
                                }}>NEW</span>
                              )}
                            </td>

                            {/* Origin country */}
                            <td>
                              <select
                                value={vendorMap[vendor] || ''}
                                onChange={(e) => handleVendorOriginChange(vendor, e.target.value)}
                                className="form-control form-control-sm"
                              >
                                <option value="">— Use default —</option>
                                {originCountryOptions.map(country => (
                                  <option key={country.code} value={country.code}>
                                    {country.name}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* Zero-rate duty — enable toggle + destination tags */}
                            <td>
                              {/* Enable checkbox */}
                              <label className="checkbox-inline" style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: (tcaEnabled[vendor] || false) ? '8px' : '0'}}>
                                <input
                                  type="checkbox"
                                  checked={tcaEnabled[vendor] || false}
                                  onChange={() => handleTcaToggle(vendor)}
                                  title="Enable zero-rate duty (0%) for this vendor. Choose which destination countries apply below."
                                  style={{cursor: 'pointer'}}
                                />
                                <small>
                                  Enable 0% duty
                                  {(tcaEnabled[vendor] || false) && <span style={{color: '#4caf50', marginLeft: '4px'}}>✓</span>}
                                </small>
                              </label>

                              {/* Destination tags + add selector (only when enabled) */}
                              {(tcaEnabled[vendor] || false) && (
                                <div style={{marginTop: '4px'}}>
                                  {/* Existing destination tags */}
                                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px'}}>
                                    {vendorDests.map(dest => {
                                      const opt = DESTINATION_OPTIONS.find(o => o.value === dest);
                                      const label = opt ? opt.label : dest;
                                      return (
                                        <span
                                          key={dest}
                                          style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                                            background: '#e3f2fd', color: '#1565c0',
                                            borderRadius: '12px', padding: '2px 8px',
                                            fontSize: '11px', fontWeight: '500',
                                            border: '1px solid #90caf9'
                                          }}
                                        >
                                          {label}
                                          <button
                                            onClick={() => handleRemoveDestination(vendor, dest)}
                                            style={{
                                              background: 'none', border: 'none', cursor: 'pointer',
                                              color: '#1565c0', fontSize: '13px', lineHeight: '1',
                                              padding: '0 0 0 2px', fontWeight: 'bold'
                                            }}
                                            title={`Remove ${label}`}
                                          >
                                            ×
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>

                                  {/* Add destination dropdown (hide if ALL already selected) */}
                                  {!vendorDests.includes('ALL') && (
                                    <select
                                      defaultValue=""
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleAddDestination(vendor, e.target.value);
                                          e.target.value = '';
                                        }
                                      }}
                                      className="form-control form-control-sm"
                                      style={{fontSize: '11px', padding: '2px 4px', height: 'auto'}}
                                      title="Add a destination country or group"
                                    >
                                      <option value="">+ Add destination...</option>
                                      {DESTINATION_OPTIONS
                                        .filter(opt => !vendorDests.includes(opt.value))
                                        .map(opt => (
                                          <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </option>
                                        ))
                                      }
                                    </select>
                                  )}
                                </div>
                              )}
                            </td>

                            {/* Compact per-vendor country picker, matching the existing DDP tile UI. */}
                            <td style={{minWidth: '240px'}}>
                              <details className={`vendor-ddp-country-picker ${!hasEnterpriseDdpAccess ? 'vendor-ddp-country-picker--locked' : ''}`} style={{position: 'relative'}} onClick={(event) => { if (!hasEnterpriseDdpAccess) event.preventDefault(); }}>
                                <summary style={{
                                  cursor: 'pointer', padding: '9px 12px', border: '1px solid #cbd5e1',
                                  borderRadius: '7px', background: '#f8fafc', fontWeight: 600,
                                  display: 'flex', justifyContent: 'space-between', gap: '8px',
                                  alignItems: 'center', listStyle: 'none'
                                }}>
                                  <span>{!hasEnterpriseDdpAccess ? '🔒 Upgrade to Enterprise for DDP' : 'DDP countries'}</span>
                                  <small style={{color: '#475569'}}>
                                    {(vendorDdpCountries[vendor] || []).includes('ALL')
                                      ? 'All countries'
                                      : (vendorDdpCountries[vendor] || []).includes('EU')
                                        ? 'EU27' + ((vendorDdpCountries[vendor] || []).filter(x => x !== 'EU').length
                                            ? ' + ' + (vendorDdpCountries[vendor] || []).filter(x => x !== 'EU').length
                                            : '')
                                        : (vendorDdpCountries[vendor] || []).length
                                          ? (vendorDdpCountries[vendor] || []).length + ' selected'
                                          : 'Off · calculator only'}
                                    {' ▾'}
                                  </small>
                                </summary>
                                <div style={{
                                  padding: '12px', marginTop: '6px', border: '1px solid #cbd5e1',
                                  borderRadius: '8px', background: '#fff'
                                }}>
                                  <p style={{fontSize: '12px', margin: '0 0 9px', color: '#475569'}}>
                                    Choose the countries where this vendor offers DDP. Unselected
                                    destinations continue using the normal duty calculator.
                                  </p>
                                  <div className="ddp-country-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                    gap: '8px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    padding: '12px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    background: '#fafafa'
                                  }}>
                                    {DESTINATION_OPTIONS.map(option => {
                                      const selected = (vendorDdpCountries[vendor] || []).includes(option.value);
                                      const implied = !selected && (
                                        (vendorDdpCountries[vendor] || []).includes('ALL') ||
                                        (option.value !== 'EU' && EU_DDP_COUNTRY_CODES.has(option.value) &&
                                          (vendorDdpCountries[vendor] || []).includes('EU'))
                                      );
                                      return (
                                        <label key={option.value}
                                          className="ddp-country-item"
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            background: selected || implied ? '#fff3e0' : '#fff',
                                            border: selected || implied ? '2px solid #ff9800' : '1px solid #ddd',
                                            borderRadius: '6px',
                                            cursor: hasEnterpriseDdpAccess ? 'pointer' : 'not-allowed',
                                            transition: 'all 0.2s ease'
                                          }}>
                                          <input
                                            type="checkbox"
                                            checked={selected || implied}
                                            disabled={!hasEnterpriseDdpAccess}
                                            onChange={() => changeVendorDdp(vendor, option.value, !selected)}
                                            style={{position: 'absolute', opacity: 0, width: 0, height: 0}}
                                          />
                                          <span style={{fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                                            {(selected || implied) && <span>✓</span>}
                                            {option.label}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <div style={{display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', marginTop: '10px', gap: '8px'}}>
                                    <button type="button" className="btn btn-sm btn-outline-secondary"
                                      disabled={!hasEnterpriseDdpAccess || !(vendorDdpCountries[vendor] || []).length}
                                      onClick={() => {
                                        const updated = {...vendorDdpCountries, [vendor]: []};
                                        setVendorDdpCountries(updated);
                                        triggerAutoSave({
                                          enabled: originMode === 'multi' && hasMultiOriginAccess(),
                                          mode: 'vendor', default_origin: defaultOrigin,
                                          vendor_origin_map: vendorMap,
                                          vendor_tca_preferences: tcaPreferences,
                                          vendor_ddp_countries: updated,
                                          origin_metafield: metafieldConfig,
                                          calculator_visibility_rules: visibilityRules
                                        });
                                      }}>Clear selection</button>
                                    <small style={{color: '#64748b'}}>Changes save automatically</small>
                                  </div>
                                </div>
                              </details>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls - Hide when searching */}
                {!vendorSearch.trim() && totalPages > 1 && (
                  <div className="vendor-pagination">
                    <div className="pagination-info">
                      Showing {startIndex + 1}–{Math.min(endIndex, totalFiltered)} of {totalFiltered} vendors
                      <span style={{margin: '0 0.5rem', color: '#999'}}>•</span>
                      Page {vendorPage} of {totalPages}
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="btn-pagination"
                        onClick={() => setVendorPage(vendorPage - 1)}
                        disabled={vendorPage === 1}
                        title="Previous page"
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Previous
                      </button>
                      <button
                        className="btn-pagination"
                        onClick={() => setVendorPage(vendorPage + 1)}
                        disabled={vendorPage >= totalPages}
                        title="Next page"
                      >
                        Next
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Collect import charges after configuring vendor DDP destinations. */}
      {originMode === 'multi' && hasMultiOriginAccess() && (
        <div style={{
          marginBottom: '16px', padding: '14px 16px', background: '#f7fbff',
          border: '1px solid #c7d7e8', borderLeft: '4px solid #007acc', borderRadius: '8px'
        }}>
          <strong>Import Duties &amp; Taxes line-item collection</strong>
          <p style={{fontSize: '13px', margin: '7px 0'}}>
            Configure vendor DDP destinations above. Collection is automatically enabled when the first destination is saved on an eligible plan; use the switch below to turn collection on or off.
          </p>
          {!hasEnterpriseDdpAccess && (
            <div className="tdk-ddp-upgrade-notice">
              <strong>🔒 Upgrade to Enterprise to unlock Multi-Origin DDP</strong>
              <p>Vendor DDP destination selection and checkout collection are available on Enterprise ($149.99/month) or Plus Checkout. Your Growth plan still includes vendor origin mapping and qualifying 0% duty settings.</p>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => onUpgrade && onUpgrade(shop)}>View upgrade options →</button>
            </div>
          )}
          {(() => {
            const hasVendorDdpDestinations = Object.values(vendorDdpCountries || {})
              .some(countries => Array.isArray(countries) && countries.length > 0);
            const collectionControlDisabled = vendorFeeCollectionBusy ||
              !vendorFeeCollectionStatusLoaded || !hasVendorDdpDestinations || !hasEnterpriseDdpAccess;
            return <>
              <label className="tdk-collection-toggle" style={{
                cursor: collectionControlDisabled ? 'not-allowed' : 'pointer',
                opacity: collectionControlDisabled ? 0.7 : 1
              }}>
                <input
                  type="checkbox"
                  role="switch"
                  checked={vendorFeeCollectionEnabled}
                  disabled={collectionControlDisabled}
                  onChange={(event) => {
                    setError(null);
                    syncVendorFeeCollection(event.target.checked);
                  }}
                />
                <span className="tdk-collection-toggle-track" aria-hidden="true" />
                <span>Collect import duties &amp; taxes at checkout</span>
              </label>
              {!hasVendorDdpDestinations && (
                <small style={{color: '#64748b', display: 'block', marginBottom: '7px'}}>
                  Select at least one vendor DDP destination before enabling collection.
                </small>
              )}
            </>;
          })()}
          <div role="status" style={{fontSize: '13px', fontWeight: 600}}>
            {vendorFeeCollectionBusy
              ? 'Status: busy — configuring Shopify import-charge component…'
              : !vendorFeeCollectionStatusLoaded
                ? 'Status: checking collection setting…'
              : vendorFeeCollectionEnabled
                ? 'Status: active — Multi-Origin DDP collection is enabled for selected countries'
                : 'Status: off — Multi-Origin DDP collection is not enabled'}
          </div>
          <small style={{color: '#64748b'}}>
            Do not also include the same import charges in Shopify Market prices.
          </small>
        </div>
      )}

      {/* Advanced Settings (Metafield Override) */}
      {originMode === 'multi' && hasMultiOriginAccess() && (
        <>
          {/* Advanced Features - Temporarily Hidden */}
          {false && (
            <div className="advanced-section">
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? '▼' : '▶'} Advanced: Metafield Override
              </button>

              {showAdvanced && (
                <div className="advanced-content">
                  <p className="help-text">
                    Configure a product/variant metafield to override vendor and default origin.
                    This metafield takes highest priority in origin resolution.
                  </p>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={metafieldConfig.enabled}
                        onChange={(e) => {
                          const newEnabled = e.target.checked;
                          const newMetafieldConfig = { ...metafieldConfig, enabled: newEnabled };
                          setMetafieldConfig(newMetafieldConfig);
                          triggerAutoSave({
                            enabled: originMode === 'multi' && hasMultiOriginAccess(),
                            mode: 'vendor',
                            default_origin: defaultOrigin,
                            vendor_origin_map: vendorMap,
                            vendor_tca_preferences: tcaPreferences,
        vendor_ddp_countries: vendorDdpCountries,
                            origin_metafield: newMetafieldConfig,
                            calculator_visibility_rules: visibilityRules
                          });
                        }}
                      />
                      <span>Enable Metafield Override</span>
                    </label>
                  </div>

                  {metafieldConfig.enabled && (
                    <>
                      <div className="form-group">
                        <label>Scope</label>
                        <select
                          value={metafieldConfig.scope}
                          onChange={(e) => setMetafieldConfig(prev => ({ ...prev, scope: e.target.value }))}
                          className="form-control"
                        >
                          <option value="product">Product</option>
                          <option value="variant">Variant</option>
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group col-md-6">
                          <label>Namespace</label>
                          <input
                            type="text"
                            value={metafieldConfig.namespace}
                            onChange={(e) => setMetafieldConfig(prev => ({ ...prev, namespace: e.target.value }))}
                            className="form-control"
                            placeholder="e.g., custom"
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>Key</label>
                          <input
                            type="text"
                            value={metafieldConfig.key}
                            onChange={(e) => setMetafieldConfig(prev => ({ ...prev, key: e.target.value }))}
                            className="form-control"
                            placeholder="e.g., origin_country"
                          />
                        </div>
                      </div>

                      <div className="alert alert-info">
                        <small>
                          <strong>Example:</strong> Create a metafield <code>{metafieldConfig.namespace}.{metafieldConfig.key}</code>
                          and set it to a 2-letter country code (e.g., "HK", "CN", "US").
                          This will override vendor mapping for that specific product.
                        </small>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Separate manual-save draft editor, never participates in legacy auto-save. */}


      {/* Auto-Save Status */}
      <div className="form-actions" style={{padding: '1rem', background: '#f8f9fa', borderRadius: '4px', marginTop: '1rem'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          {saving && (
            <>
              <span style={{fontSize: '16px'}}>💾</span>
              <span style={{color: '#666'}}>Saving changes...</span>
            </>
          )}
          {!saving && success && (
            <>
              <span style={{fontSize: '16px'}}>✅</span>
              <span style={{color: '#4caf50'}}>All changes saved automatically</span>
            </>
          )}
          {!saving && !success && (
            <>
              <span style={{fontSize: '16px'}}>ℹ️</span>
              <span style={{color: '#666'}}>Changes will auto-save</span>
            </>
          )}
        </div>

        {config && config._plan_restricted && (
          <div style={{marginTop: '0.5rem'}}>
            <small style={{color: '#f57c00'}}>⚠️ Multi-origin is saved but inactive (upgrade to activate)</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiOriginSettings;
